/**
 * Parse a date string and return it as a Date object
 * The database stores times as UTC, but we want to display them as local time
 * So we extract the time components and create a local date
 */
export const parseLocalDate = (dateString: string): Date => {
  if (!dateString) return new Date();
  
  // Parse the UTC date
  const utcDate = new Date(dateString);
  
  // Extract the time components (these represent the local time we want to display)
  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth();
  const day = utcDate.getUTCDate();
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes();
  const second = utcDate.getUTCSeconds();
  
  // Create a new date using these components as local time
  return new Date(year, month, day, hour, minute, second);
};

/**
 * Format a date string for display
 */
export const formatDate = (dateString: string): string => {
  const utcDate = new Date(dateString);
  
  // Function to convert number to ordinal
  const getOrdinal = (num: number) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) {
      return num + "st";
    }
    if (j === 2 && k !== 12) {
      return num + "nd";
    }
    if (j === 3 && k !== 13) {
      return num + "rd";
    }
    return num + "th";
  };
  
  // Use UTC methods to get the exact time without timezone conversion
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][utcDate.getUTCDay()];
  const day = getOrdinal(utcDate.getUTCDate());
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][utcDate.getUTCMonth()];
  const year = utcDate.getUTCFullYear();
  
  // Format time in 12-hour format
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  
  return `${weekday} ${day} ${month} ${year}, ${displayHour}:${minute}${ampm}`;
};

/**
 * Format a date string for display (date only)
 */
export const formatDateOnly = (dateString: string): string => {
  const utcDate = new Date(dateString);
  const day = utcDate.getUTCDate();
  const month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][utcDate.getUTCMonth()];
  const year = utcDate.getUTCFullYear();
  
  return `${day} ${month} ${year}`;
};

/**
 * Format a date string for display (date only with ordinals)
 */
export const formatDateOrdinal = (dateString: string): string => {
  const utcDate = new Date(dateString);
  
  // Function to convert number to ordinal
  const getOrdinal = (num: number) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) {
      return num + "st";
    }
    if (j === 2 && k !== 12) {
      return num + "nd";
    }
    if (j === 3 && k !== 13) {
      return num + "rd";
    }
    return num + "th";
  };
  
  const day = getOrdinal(utcDate.getUTCDate());
  const month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][utcDate.getUTCMonth()];
  const year = utcDate.getUTCFullYear();
  
  return `${day} ${month} ${year}`;
};

/**
 * Format a date string for display (time only)
 */
export const formatTimeOnly = (dateString: string): string => {
  const utcDate = new Date(dateString);
  const hour = utcDate.getUTCHours().toString().padStart(2, '0');
  const minute = utcDate.getUTCMinutes().toString().padStart(2, '0');
  
  return `${hour}:${minute}`;
};

/**
 * Format a date string for display (short format: "Tue 9th Dec, 7:45pm")
 */
export const formatDateShort = (dateString: string): string => {
  const utcDate = new Date(dateString);
  
  // Function to convert number to ordinal
  const getOrdinal = (num: number) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) {
      return num + "st";
    }
    if (j === 2 && k !== 12) {
      return num + "nd";
    }
    if (j === 3 && k !== 13) {
      return num + "rd";
    }
    return num + "th";
  };
  
  // Use UTC methods to get the exact time without timezone conversion
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][utcDate.getUTCDay()];
  const day = getOrdinal(utcDate.getUTCDate());
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][utcDate.getUTCMonth()];
  
  // Format time in 12-hour format
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  
  // return `${weekday} ${day} ${month}, ${displayHour}:${minute}${ampm}`;
  return `${weekday} ${day} ${month}`;
};

const getUTCDateTimestamp = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Whether today (UTC calendar date) is on or after the match's calendar date.
 */
export const isOnOrAfterMatchDay = (matchDateString: string, now: Date = new Date()): boolean => {
  const matchDate = new Date(matchDateString);
  return getUTCDateTimestamp(now) >= getUTCDateTimestamp(matchDate);
};

/**
 * Treat stored UTC components as local wall-clock time (matches how dates are displayed).
 */
const getStoredLocalTime = (dateString: string): Date => {
  const date = new Date(dateString);
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
};

/**
 * Whether the match kickoff time has passed.
 */
export const hasMatchStarted = (matchDateString: string, now: Date = new Date()): boolean => {
  return now.getTime() >= getStoredLocalTime(matchDateString).getTime();
};

/**
 * Whether the prediction window has closed for this match.
 */
export const havePredictionsClosed = (predictionsCloseString: string, now: Date = new Date()): boolean => {
  return now.getTime() >= getStoredLocalTime(predictionsCloseString).getTime();
};

/**
 * Whether other users' league predictions should be visible.
 * Requires kickoff; also requires predictions close when set (prevents early reveal if kickoff is misconfigured).
 */
export const shouldRevealLeaguePrediction = (
  matchDateString: string,
  predictionsCloseString: string | undefined,
  now: Date = new Date()
): boolean => {
  if (!hasMatchStarted(matchDateString, now)) {
    return false;
  }

  if (predictionsCloseString) {
    return havePredictionsClosed(predictionsCloseString, now);
  }

  return true;
};

/**
 * Format a date as a relative time string (e.g. "2m ago", "1h ago")
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  const day = date.getUTCDate();
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getUTCMonth()];
  return `${day} ${month}`;
}; 